import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { faDownload, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { TextField } from "@mui/material";

import { faCopy } from "@fortawesome/pro-light-svg-icons";

import { usePageTitle } from "@/utils/usePageTitle";

import {
	useDeleteCoursesMutation,
	useGetCourseQuery,
	useGetRelatedCoursesQuery,
} from "@/pages/Private/redux/courses/courses.api";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { AiGeneratedBadge } from "@/components/AiGeneratedBadge/AiGeneratedBadge";
import VideoContainer from "@/components/VideoUploader/VideoContainer";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import EditPen from "@assets/icons/pen.svg?react";

import { Tab, TabsContainer } from "@/components/Tabs/TabsContainer";

import { PermissionRoles } from "@/enum";

import { ROUTE_CONFIG } from "@/routes/config";

import { useLazyDownloadCourseQuery } from "@/pages/Private/redux/course/course.api";

import CourseTag from "../../Courses/components/CourseList/CourseTag";
import StartCourseModal from "../../Courses/components/StartCourseModal";
import { FileAssignment } from "../../Courses/enums/FileAssignment.enum";
import { CourseType } from "../schema/course";
import Breadcrumbs from "./Breadcrumbs";
import RelatedCoursesContainer from "./CourseDetails/RelatedCoursesContainer";
import DuplicateCourse from "./DuplicateCourse";

type CourseDetailsProps = {
	company?: CourseType;
};

export const CourseDetails: FunctionComponent<CourseDetailsProps> = ({}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`course.${key}`);
	const td = (key: string) => t(`courses.duplicate.${key}`);

	const { id = 0 } = useParams();
	const { data, isFetching } = useGetCourseQuery(+id);
	const { data: relatedCoursesData, isFetching: isRelatedCoursesDataFetching } =
		useGetRelatedCoursesQuery(+id);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [isStaringCourseModalOpen, setIsStaringCourseModalOpen] = useState<boolean>(false);
	const [deleteCourses, { isLoading: isDeleteLoading }] = useDeleteCoursesMutation();
	const navigate = useNavigate();
	const [downloadCourse, { isLoading: isDownloadLoading }] = useLazyDownloadCourseQuery();
	const [copyTitle, setCopyTitle] = useState<string>("");
	const [showCopyModal, setShowCopyModal] = useState<boolean>(false);

	const { trailerVideo } = useMemo(() => {
		const trailerVideo = data?.courseFiles?.find(
			(file) => file.fileAssignment === FileAssignment.TrailerVideo
		);

		return { trailerVideo };
	}, [data]);

	usePageTitle(ts("title"));

	const handleDeleteModal = () => {
		if (data?.id) {
			setShowDeleteModal(true);
		}
	};

	const handleDeleteCourses = async () => {
		if (!data?.id) {
			return;
		}

		try {
			await deleteCourses(data?.id ?? 0).unwrap();
			setShowDeleteModal(false);
			navigate(ROUTE_CONFIG.COURSES);
		} catch (err) {
			console.error(err);
		}
	};

	const handleDownload = async (id: number) => {
		try {
			const downloadPromise = await downloadCourse({ id }).unwrap();

			const sanitizedFileName = data?.name
				? `${data.name
						.replace(/[<>:"/\\|?*]/g, "_") // Remove invalid filename characters
						.replace(/\s+/g, "_") // Replace spaces with underscores
						.replace(/\./g, "_") // Replace dots with underscores
						.replace(/'/g, "_") // Replace single quotes with underscores
						.trim()}.xlsx`
				: "scenario.xlsx";

			const url = URL.createObjectURL(downloadPromise);

			const a = document.createElement("a");

			a.href = url;

			a.download = sanitizedFileName;

			a.rel = "noopener";

			// Firefox is happier when the element is in the DOM
			document.body.appendChild(a);
			a.click();
			a.remove();

			// Revoke the URL after a tick to let the download start
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch (error) {
			console.error(error);
		}
	};

	if (isFetching) {
		return <LoadingOverlay />;
	}

	function handleCopyModal() {
		setShowCopyModal(true);
		setCopyTitle(data?.name + ` (${td("copy")})`);
	}

	return (
		<>
			<div className="w-full flex-1">
				<Breadcrumbs
					items={[
						{
							title: ts("all-scenarios"),
							url: ROUTE_CONFIG.COURSES,
						},
						{
							title: data?.name ?? "",
							url: `${ROUTE_CONFIG.COURSES}/${id}`,
						},
					]}
				/>
				<div className="p-6 flex gap-6">
					<div className="w-[75%] flex-1 space-y-6">
						{trailerVideo?.fullFilePath && (
							<div className="space-y-3">
								{/* Above the player rather than on it: the marker has to be legible to
								    someone who is not looking for it, which is what the transparency
								    obligation is actually about. */}
								<AiGeneratedBadge isGenerated={trailerVideo.aiGenerated} />
								{/* No caption track here yet, deliberately. The export's subtitles are
								    burnt into this file, and its `.vtt` sidecar is a media asset rather
								    than a course-file — a caption file has no `FileAssignment` that
								    fits — so this endpoint, which serves course-files, cannot reach it.
								    `VideoContainer` takes the track already; the day captions should
								    show here, the sidecar becomes a course-file and the URL is passed
								    in. */}
								<VideoContainer
									isDefaultControlsEnabled
									isShowDeleteButton={false}
									videoContainerClassName="h-[500px]"
									videoUrl={trailerVideo?.fullFilePath ?? null}
								/>
							</div>
						)}
						<TabsContainer
							addiTionalHeaderComponent={
								<div className="w-full flex items-center justify-end gap-2">
									<PermissionCheck requiredPermissions={[PermissionRoles.USER]}>
										<Button
											className="!h-[44px] !font-medium !text-[14px] !px-6 !w-fit !rounded-xl"
											color={ButtonColor.ACTION}
											size={ButtonSize.ML}
											title={ts("get-started")}
											onClick={() => setIsStaringCourseModalOpen(true)}
										></Button>
									</PermissionCheck>
									<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
										<>
											<Button
												className="!h-[44px] !min-w-[44px] !max-w-[44px] !p-0 !rounded-xl"
												color={ButtonColor.ACTION_SECONDARY}
												image={<SvgIcon className="w-5 h-5" svgIcon={EditPen} />}
												size={ButtonSize.ML}
												title=""
												onClick={() => navigate(`${ROUTE_CONFIG.COURSES}/edit/${id}`)}
											></Button>

											<Button
												className="!h-[44px] !min-w-[44px] !max-w-[44px] !p-0 !rounded-xl"
												color={ButtonColor.ACTION_SECONDARY}
												image={<Icon className="w-5 h-5" icon={faCopy} />}
												size={ButtonSize.ML}
												title=""
												onClick={() => handleCopyModal()}
											></Button>
											<Button
												className="!h-[44px] !min-w-[44px] !max-w-[44px] !p-0 !rounded-xl"
												color={ButtonColor.ACTION_SECONDARY}
												image={<Icon className="w-5 h-5" icon={faDownload} />}
												isLoading={isDownloadLoading}
												size={ButtonSize.ML}
												title=""
												onClick={() => handleDownload(+(id || 0))}
											></Button>
											<Button
												className="!h-[44px] !min-w-[44px] !max-w-[44px] !p-0 !rounded-xl"
												color={ButtonColor.ACTION_SECONDARY}
												image={<Icon className="w-5 h-5" icon={faTrashCan} />}
												size={ButtonSize.ML}
												title=""
												onClick={() => handleDeleteModal()}
											></Button>
										</>
									</PermissionCheck>
								</div>
							}
						>
							<Tab title={ts("overview")}>
								<h3 className="text-[28px] font-semibold text-primary-gray mb-3">
									{data?.name ?? ""}
								</h3>
								<CourseTag className="mb-3 w-fit text-primary-blue-hover" tag={data?.tag?.name} />
								<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
									<CourseTag
										className="mb-3 w-fit text-primary-blue-hover"
										tag={t(`courses.language.${data?.language === "DE" ? "deutsch" : "english"}`)}
									/>
								</PermissionCheck>

								<p className="mt-3 text-primary-gray-lighter text-[14px]">
									{data?.description ?? ""}
								</p>
							</Tab>
							<Tab title={ts("for-whom")}>
								<h3 className="text-[28px] font-semibold text-primary-gray mb-3">
									{ts("for-whom")}
								</h3>
								<p className="text-primary-gray-lighter text-[14px]">{data?.forWhom ?? ""}</p>
							</Tab>
						</TabsContainer>
					</div>
					<div className="w-[25%] space-y-3">
						<RelatedCoursesContainer
							isRelatedCoursesDataFetching={isRelatedCoursesDataFetching}
							relatedCourses={relatedCoursesData ?? []}
						/>
					</div>
				</div>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteCourses}
					isLoading={isDeleteLoading}
					isOpened={showDeleteModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={t("courses.delete.title")}
				>
					<TextField
						fullWidth
						defaultValue={data?.name}
						inputProps={{ readOnly: true }}
						label={t("basics.name")}
					/>
				</Modal>
			)}
			{isStaringCourseModalOpen && data?.id && (
				<StartCourseModal
					isOpen={isStaringCourseModalOpen}
					setIsOpen={setIsStaringCourseModalOpen}
					trainingInfo={data}
				/>
			)}

			<PermissionCheck requiredPermissions={[PermissionRoles.GLOBAL_ADMIN]}>
				{setShowCopyModal && (
					<DuplicateCourse
						copyTitle={copyTitle}
						setCopyTitle={setCopyTitle}
						setShowCopyModal={setShowCopyModal}
						showCopyModal={showCopyModal}
					/>
				)}
			</PermissionCheck>
		</>
	);
};
